import styled, { css, keyframes } from 'styled-components';
import { ob } from 'components/Onboarding';

/* ------------------------------------------------------------------ */
/* Pricing configuration tabs + small helpers                          */
/* ------------------------------------------------------------------ */

export const ConfigHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  .label {
    font-size: 13px;
    font-weight: 600;
  }
`;

export const Segmented = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  padding: 4px;
  border: 1px solid ${ob.border};
  border-radius: 999px;
  background: ${ob.surface};
  button {
    height: 32px;
    padding: 0 16px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: ${ob.muted};
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 140ms ease;
    &:hover {
      color: ${ob.text};
    }
    &[aria-pressed='true'] {
      background: ${ob.primary};
      color: #fff;
      box-shadow: 0 1px 2px rgba(26, 26, 26, 0.08);
    }
  }
`;

export const SubLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${ob.muted};
`;

export const TwoCol = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 12px;
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const InlineLink = styled.button`
  align-self: flex-start;
  border: 0;
  background: none;
  padding: 0;
  color: ${ob.primary};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const Callout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(241, 238, 232, 0.6);
  svg {
    margin-top: 2px;
    flex-shrink: 0;
    color: ${ob.muted};
  }
  .t {
    font-size: 14px;
    font-weight: 600;
  }
  .s {
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.5;
    color: ${ob.muted};
  }
`;

export const NoteLine = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${ob.muted};
  strong {
    color: rgba(26, 26, 26, 0.8);
  }
`;

export const StatusBanner = styled.div`
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid #9ae6b4;
  border-left: 3px solid ${ob.success};
  border-radius: 12px;
  background: ${ob.successSoft};
  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: #2f855a;
  }
  p:first-child {
    font-weight: 700;
    color: #276749;
  }
`;

export const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

/** Input with an inline action button (Generate / Copy). */
export const InlineAction = styled.div`
  display: flex;
  height: 48px;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 0 6px 0 14px;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: ${ob.surface};
  box-shadow: 0 1px 1px rgba(26, 26, 26, 0.03);
  &:focus-within {
    border-color: ${ob.primary};
    box-shadow: 0 0 0 3px ${ob.ring};
  }
  input {
    min-width: 0;
    flex: 1;
    height: 100%;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 14px;
    color: ${ob.text};
    outline: none;
    text-overflow: ellipsis;
    &::placeholder {
      color: ${ob.muted};
    }
  }
`;

export const StepsList = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.5;
  span {
    display: block;
    margin-top: 4px;
  }
  span:first-child {
    margin-top: 0;
  }
`;

/* ------------------------------------------------------------------ */
/* Calendar (react-calendar skinned to the new host UI)                */
/* ------------------------------------------------------------------ */

export const CalendarSkin = styled.div`
  && .react-calendar {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    border: 1px solid ${ob.border};
    border-radius: 16px;
    background: #fff;
    box-shadow: ${ob.shadowCard};
    font-family: ${ob.fontBody};
    line-height: 1.2;
  }
  && .react-calendar__navigation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: auto;
    margin: 0;
    padding: 14px 12px;
    @media (min-width: 640px) {
      padding: 16px 20px;
    }
  }
  && .react-calendar__navigation button {
    min-width: 0;
    background: transparent;
    font-family: inherit;
  }
  && .react-calendar__navigation__arrow {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border: 1px solid rgba(238, 235, 230, 0.9);
    border-radius: 50%;
    color: ${ob.text};
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    transition: background 140ms ease;
  }
  && .react-calendar__navigation__arrow:hover:enabled {
    background: ${ob.mutedBg};
  }
  && .react-calendar__navigation__arrow:disabled {
    opacity: 0.3;
    cursor: default;
  }
  && .react-calendar__navigation__label {
    flex: 1;
    border: 0;
    font-family: ${ob.fontDisplay};
    font-size: 16px;
    font-weight: 700;
    color: ${ob.text};
    pointer-events: none;
    @media (min-width: 640px) {
      font-size: 18px;
    }
  }
  && .react-calendar__month-view__weekdays {
    padding: 8px 0;
    border-top: 1px solid rgba(238, 235, 230, 0.7);
    border-bottom: 1px solid rgba(238, 235, 230, 0.7);
    background: rgba(241, 238, 232, 0.3);
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${ob.muted};
  }
  && .react-calendar__month-view__weekdays__weekday {
    padding: 0;
  }
  && .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
  }
  /* react-calendar lays days out as 7 equal flex items and offsets the first tile with an inline
     margin — keep that model (a CSS grid here shifted every week by the month's start day). */
  && .react-calendar__month-view__days {
    display: flex !important;
    flex-wrap: wrap;
    margin-right: -1px;
  }
  && .react-calendar__tile {
    position: relative;
    display: flex;
    flex: 0 0 14.2857% !important;
    max-width: 14.2857% !important;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    height: 72px;
    margin: 0;
    padding: 6px 8px;
    border: 0;
    border-right: 1px solid rgba(238, 235, 230, 0.6);
    border-bottom: 1px solid rgba(238, 235, 230, 0.6);
    border-radius: 0;
    background: #fff;
    color: ${ob.text};
    font-family: ${ob.fontBody};
    text-align: left;
    cursor: pointer;
    transition: background 140ms ease;
    @media (min-width: 640px) {
      height: 88px;
      padding: 10px 12px;
    }
  }
  && .react-calendar__tile abbr {
    font-family: ${ob.fontDisplay};
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    text-decoration: none;
    @media (min-width: 640px) {
      font-size: 16px;
    }
  }
  && .react-calendar__tile:enabled:hover,
  && .react-calendar__tile:enabled:focus {
    background: rgba(255, 240, 230, 0.55);
  }
  && .react-calendar__tile:disabled {
    background: #fff;
    color: rgba(107, 107, 107, 0.4);
    cursor: not-allowed;
  }
  && .react-calendar__tile.pc-blocked:enabled {
    background: repeating-linear-gradient(
      135deg,
      transparent 0 7px,
      rgba(241, 238, 232, 0.9) 7px 8px
    );
  }
  && .react-calendar__tile.pc-blocked abbr {
    color: rgba(107, 107, 107, 0.7);
  }
  && .react-calendar__tile.pc-booked:enabled {
    background: rgba(238, 244, 255, 0.8);
  }
  && .react-calendar__tile.pc-selected,
  && .react-calendar__tile.pc-selected:enabled:hover,
  && .react-calendar__tile.pc-selected:enabled:focus {
    background: ${ob.primarySoft};
    box-shadow: inset 0 0 0 1.5px ${ob.primary};
  }
  && .react-calendar__tile.pc-selected abbr {
    color: ${ob.primary};
  }
`;

export const TileInfo = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  .price {
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: rgba(26, 26, 26, 0.9);
    @media (min-width: 640px) {
      font-size: 16px;
    }
  }
  .price.blocked {
    color: rgba(107, 107, 107, 0.6);
  }
  .meta {
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    color: ${ob.primary};
    @media (min-width: 640px) {
      font-size: 10.5px;
    }
  }
  .meta.restricted {
    color: ${ob.warning};
  }
`;

/**
 * The bar a booking draws across its nights. Each tile draws its own piece: the pieces of one stay join across the tile
 * borders and are rounded where the stay starts and ends.
 */
export const StayBar = styled.div<{ $start: boolean; $end: boolean; $other: boolean }>`
  position: absolute;
  left: ${({ $start }) => ($start ? '6px' : '-1px')};
  right: ${({ $end }) => ($end ? '6px' : '-1px')};
  bottom: 10px;
  height: 26px;
  border-radius: ${({ $start, $end }) => `${$start ? '13px' : '0'} ${$end ? '13px' : '0'} ${$end ? '13px' : '0'} ${$start ? '13px' : '0'}`};
  background: ${({ $other }) => ($other ? '#4b4b4b' : '#222222')};
  pointer-events: none;
  z-index: 1;
`;

/** The text of the bar: the guest (with an initial) for a booking of ours, "Booked" for one made elsewhere. */
export const StayLabel = styled.div<{ $start: boolean; $span: number }>`
  position: absolute;
  left: ${({ $start }) => ($start ? '6px' : '0')};
  bottom: 10px;
  width: calc(${({ $span }) => $span} * 100% - ${({ $start }) => ($start ? '12px' : '6px')});
  height: 26px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px 0 4px;
  box-sizing: border-box;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  pointer-events: none;
  z-index: 2;
  .avatar {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #f8e0cf;
    color: #a24d12;
    font-size: 11px;
    font-weight: 700;
  }
  .text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const LoadingNote = styled.div`
  padding: 40px 0;
  text-align: center;
  font-size: 14px;
  color: ${ob.muted};
`;

/* ------------------------------------------------------------------ */
/* Date actions bottom sheet                                           */
/* ------------------------------------------------------------------ */

const slideUp = keyframes`
  from { transform: translateX(-50%) translateY(100%); }
  to { transform: translateX(-50%) translateY(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Sheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 200;
  width: 100%;
  max-width: 560px;
  overflow: hidden;
  border-radius: 24px 24px 0 0;
  background: #fff;
  box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.14);
  transform: translateX(-50%);
  animation: ${slideUp} 0.22s ease;
  padding-bottom: env(safe-area-inset-bottom, 0);
  font-family: ${ob.fontBody};
`;

export const SheetHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px 12px;
  .pill {
    font-family: ${ob.fontDisplay};
    font-size: 16px;
    font-weight: 700;
    color: ${ob.text};
  }
  .close {
    position: absolute;
    right: 16px;
    top: 50%;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    transform: translateY(-50%);
    border: 0;
    border-radius: 50%;
    background: ${ob.mutedBg};
    color: ${ob.muted};
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
  }
`;

export const SheetRow = styled.div<{ $button?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border: 0;
  border-top: 1px solid rgba(238, 235, 230, 0.8);
  background: #fff;
  font-family: inherit;
  text-align: left;
  ${({ $button }) =>
    $button
      ? css`
          cursor: pointer;
          &:hover {
            background: ${ob.surface};
          }
        `
      : ''}
  .left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .label {
    font-size: 15px;
    font-weight: 600;
    color: ${ob.text};
  }
  .sub {
    font-size: 12px;
    color: ${ob.muted};
  }
  .right {
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${ob.muted};
  }
  .value {
    font-size: 15px;
    font-weight: 600;
    color: ${ob.text};
  }
  .value.empty {
    color: ${ob.primary};
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .dot.available {
    background: ${ob.success};
  }
  .dot.blocked {
    background: ${ob.danger};
  }
  .dot.mixed {
    background: ${ob.primary};
  }
`;

export const ToggleGroup = styled.div`
  display: flex;
  gap: 8px;
  button {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border: 1px solid ${ob.border};
    border-radius: 50%;
    background: #fff;
    color: ${ob.muted};
    font-size: 16px;
    cursor: pointer;
    transition: all 140ms ease;
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
  .block:hover:not(:disabled),
  .block.on {
    border-color: ${ob.danger};
    background: rgba(190, 18, 60, 0.08);
    color: ${ob.danger};
  }
  .unblock:hover:not(:disabled),
  .unblock.on {
    border-color: ${ob.success};
    background: ${ob.successSoft};
    color: ${ob.success};
  }
`;

/* ------------------------------------------------------------------ */
/* Price entry (full screen) + modals                                   */
/* ------------------------------------------------------------------ */

export const FullScreen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  display: flex;
  flex-direction: column;
  background: #fff;
  font-family: ${ob.fontBody};
  animation: ${fadeUp} 0.2s ease;
  .inner {
    display: flex;
    height: 100%;
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
    flex-direction: column;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
  .head {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 20px 12px;
    border-bottom: 1px solid ${ob.border};
  }
  .title {
    font-family: ${ob.fontDisplay};
    font-size: 16px;
    font-weight: 700;
  }
  .close {
    position: absolute;
    right: 16px;
    top: 50%;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    transform: translateY(-50%);
    border: 0;
    border-radius: 50%;
    background: ${ob.mutedBg};
    color: ${ob.muted};
    font-size: 20px;
    cursor: pointer;
  }
  .display {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 24px;
  }
  .cur {
    font-size: 32px;
    font-weight: 600;
    color: ${ob.muted};
  }
  .amount {
    width: 220px;
    border: 0;
    background: transparent;
    font-family: ${ob.fontDisplay};
    font-size: 48px;
    font-weight: 700;
    color: ${ob.text};
    text-align: left;
    outline: none;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }
    &::placeholder {
      color: rgba(107, 107, 107, 0.35);
    }
  }
  .hint {
    padding: 0 20px 12px;
    text-align: center;
    font-size: 13px;
    color: ${ob.muted};
  }
  .save {
    margin: 0 20px 16px;
    height: 48px;
    border: 0;
    border-radius: 999px;
    background: ${ob.primary};
    color: #fff;
    box-shadow: ${ob.shadowBrand};
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  .pad {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    border-top: 1px solid ${ob.border};
    background: ${ob.border};
  }
  .key {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 64px;
    padding: 20px 8px;
    border: 0;
    background: #fff;
    font-family: inherit;
    font-size: 22px;
    font-weight: 500;
    color: ${ob.text};
    cursor: pointer;
    transition: background 120ms ease;
    &:active {
      background: ${ob.mutedBg};
    }
  }
  .key.empty {
    background: ${ob.surface};
    cursor: default;
  }
  .key.back {
    font-size: 20px;
    color: ${ob.muted};
  }
`;

export const Overlay = styled.div<{ $bottom?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: ${({ $bottom }) => ($bottom ? 'flex-end' : 'center')};
  justify-content: center;
  background: rgba(26, 26, 26, 0.45);
  font-family: ${ob.fontBody};
`;

export const Modal = styled.div`
  width: 90%;
  max-width: 420px;
  max-height: 85vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 24px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: ${ob.fontDisplay};
    font-size: 17px;
    font-weight: 700;
  }
  .close {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 50%;
    background: ${ob.mutedBg};
    color: ${ob.muted};
    font-size: 18px;
    cursor: pointer;
  }
  .sub {
    margin: 6px 0 0;
    font-size: 13px;
    color: ${ob.muted};
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 20px 0;
    font-size: 14px;
    font-weight: 600;
  }
  .stepper {
    display: flex;
    align-items: center;
    gap: 12px;
    button {
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border: 1px solid ${ob.borderStrong};
      border-radius: 50%;
      background: #fff;
      font-size: 18px;
      cursor: pointer;
      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
    span {
      min-width: 20px;
      text-align: center;
      font-size: 16px;
      font-weight: 600;
    }
  }
  .apply {
    width: 100%;
    height: 44px;
    border: 0;
    border-radius: 999px;
    background: ${ob.primary};
    color: #fff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  .divider {
    height: 1px;
    margin: 8px 0 20px;
    border: 0;
    background: ${ob.border};
  }
  .block {
    margin-bottom: 20px;
  }
  .block-label {
    display: block;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
  }
`;

export const BookedModal = styled.div`
  width: min(560px, 100%);
  max-height: 80vh;
  overflow: auto;
  padding: 20px;
  border-radius: 24px 24px 0 0;
  background: #fff;
  h3 {
    margin: 0 0 6px;
    font-family: ${ob.fontDisplay};
    font-size: 18px;
    color: ${ob.text};
  }
  .lead {
    margin: 0 0 16px;
    font-size: 14px;
    color: ${ob.muted};
  }
  .card {
    padding: 14px;
    border: 1px solid ${ob.border};
    border-radius: 12px;
    background: ${ob.surface};
  }
  .card + .card {
    margin-top: 12px;
  }
  .guest {
    margin-bottom: 6px;
    font-size: 15px;
    font-weight: 600;
  }
  .line {
    font-size: 14px;
    color: ${ob.text};
  }
  .muted {
    margin-top: 6px;
    font-size: 13px;
    color: ${ob.muted};
  }
  .stay {
    margin-top: 8px;
    font-size: 12px;
    color: ${ob.muted};
  }
  .close {
    margin-top: 16px;
    width: 100%;
    height: 44px;
    border: 1px solid ${ob.borderStrong};
    border-radius: 999px;
    background: #fff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
`;
